import {useTranslation} from '@/translations/i18n'
import type {AgendaEventAppointment, AgendaEventType} from '@/features/agenda/types'
import {eventTypeMapToLabel} from '@/features/agenda/utils'
import {useAgendaNavigation} from '@/features/agenda/hooks/useAgendaNavigation'

type TargetEntity = {
    entity: {
        fullName: string
    } | null
}

interface UseAgendaAppointmentEventDataProps {
    agendaEvent: AgendaEventAppointment | null
}

interface AgendaEventData {
    targetEntities: TargetEntity[]
    accessTypes: string
    fullNames: string
    sector?: AgendaEventType['sector']
}

export const useAgendaAppointmentEventData = ({agendaEvent}: UseAgendaAppointmentEventDataProps): AgendaEventData => {
    const {t} = useTranslation()
    const {
        params: {entity}
    } = useAgendaNavigation()

    if (!agendaEvent) return {targetEntities: [], accessTypes: '', fullNames: '', sector: undefined}

    const {eventConsultants, eventPatients, isGroupTreatment} = agendaEvent

    const targetEntities: TargetEntity[] =
        entity === 'patients'
            ? eventConsultants?.map(ep => ({entity: ep.user})) ?? []
            : eventPatients?.map(ec => ({entity: ec.patient})) ?? []

    const accessTypes = isGroupTreatment
        ? t('agenda:group_treatment')
        : eventConsultants
          ? eventConsultants
                ?.filter(
                    (ec, index, self) =>
                        index === self.findIndex(e => e.accessType.accessType === ec.accessType.accessType)
                )
                ?.map(ec => t(eventTypeMapToLabel[ec.accessType.accessType]))
                .join(', ')
          : t('agenda:unknown_event_name')

    const fullNames = targetEntities
        ? targetEntities.length > 2
            ? `${targetEntities[0].entity?.fullName}, ${targetEntities[1].entity?.fullName} +${targetEntities.length - 2}`
            : targetEntities.map(ec => ec.entity?.fullName).join(', ')
        : ''

    const sector = agendaEvent.type == 'event' ? agendaEvent.sector : undefined

    return {
        targetEntities,
        accessTypes,
        fullNames,
        sector
    }
}
